import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { apiGetCheckoutPaymentConfirmationData } from 'src/api/checkout';
import { useAnyRequestState } from 'src/utils/hooks/useAnyRequestState';
import { CheckoutConfirmationLayout } from 'src/pages/Checkout/components/common/Layout';
import { CheckoutConfirmationFundraising } from 'src/pages/Checkout/components/Fundraising';
import { CheckoutConfirmationThankYou } from 'src/pages/Checkout/components/ThankYou';
import { CheckoutConfirmationCheckInformation } from 'src/pages/Checkout/components/CheckInformation';
import { CheckoutConfirmationRegistrationLines } from 'src/pages/Checkout/components/RegistrationLines';
import { CheckoutConfirmationItemsSummary } from 'src/pages/Checkout/components/common/ItemsSummary';
import { CheckoutConfirmationPaymentsSummary } from 'src/pages/Checkout/components/PaymentsSummary';
import { CheckoutConfirmationPaymentMethodSubsection } from 'src/pages/Checkout/ContributionConfirmation/components/PaymentMethodSubsection';
import { changePageTitle } from 'src/utils/hooks/usePageTitle';
import { useStateForm } from 'src/utils/stateForm';
import {
  CheckoutConfirmationResponseType,
  CheckoutConfirmationSavePreferencesRequestType,
} from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { CheckoutConfirmationBidExplainerText } from 'src/pages/Checkout/components/BidExplainerText';
import { CheckoutConfirmationCheckInInstructions } from 'src/pages/Checkout/components/CheckInInstructions';
import { QrCodeCheckIn } from 'src/pages/Checkout/components/QrCodeCheckIn';

export const CheckoutConfirmationPage: FC = () => {
  const tHelper = useTHelper('checkout.confirmation');

  const { id } = useParams<{ id?: string }>();

  const formProps = useStateForm<CheckoutConfirmationSavePreferencesRequestType>();

  const [model, { loading }] = useAnyRequestState<CheckoutConfirmationResponseType>(
    'getCheckoutPaymentConfirmationData',
    {
      data: id,
      apiFunction: apiGetCheckoutPaymentConfirmationData,
      onSuccess: (response) => {
        changePageTitle(response.Title);

        formProps.reset({
          ContributionGuid: response.ContributionGuid,
        });
      },
    }
  );

  return (
    <CheckoutConfirmationLayout model={model} loading={loading} formProps={formProps}>
      {model && (
        <>
          {model.FundraisingPageSubsection.IsVisible && (
            <CheckoutConfirmationFundraising model={model.FundraisingPageSubsection} />
          )}
          {model.ThankYouSubsection.IsVisible && <CheckoutConfirmationThankYou model={model.ThankYouSubsection} />}
          {model.CheckInInstructionsSubsection.IsVisible && (
            <CheckoutConfirmationCheckInInstructions model={model.CheckInInstructionsSubsection} />
          )}

          {model.QrCodeCheckInSubsection.IsVisible && <QrCodeCheckIn model={model.QrCodeCheckInSubsection} />}

          {model.CheckInformationSubsection.IsVisible && (
            <CheckoutConfirmationCheckInformation model={model.CheckInformationSubsection} />
          )}
          {model.RegistrationLinesSubsection.IsVisible && (
            <CheckoutConfirmationRegistrationLines model={model.RegistrationLinesSubsection} />
          )}

          {model.ShowSummarySection && (
            <>
              <CheckoutConfirmationItemsSummary model={model} />

              {model.ShowPaymentsSummarySection && (
                <>
                  <h2 className="section-header">{model.PaymentSummaryTitle}</h2>
                  {model.ChargedNowPaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.ChargedNowPaymentSubsection} />
                  )}
                  {((model.BidPaymentSubsection.IsVisible && model.BidPaymentSubsection.IsChargedLaterLabelVisible) ||
                    (model.PledgePaymentSubsection.IsVisible &&
                      model.PledgePaymentSubsection.IsChargedLaterLabelVisible) ||
                    (model.AuctionGiftPaymentSubsection.IsVisible &&
                      model.AuctionGiftPaymentSubsection.IsChargedLaterLabelVisible)) && (
                    <div className="subsection-header">{tHelper('chargedLater')}</div>
                  )}
                  {model.BidPaymentSubsection.IsVisible && (
                    <>
                      <CheckoutConfirmationBidExplainerText />
                      <CheckoutConfirmationPaymentsSummary model={model.BidPaymentSubsection} />
                    </>
                  )}
                  {model.AuctionGiftPaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.AuctionGiftPaymentSubsection} />
                  )}
                  {model.PledgePaymentSubsection.IsVisible && (
                    <CheckoutConfirmationPaymentsSummary model={model.PledgePaymentSubsection} />
                  )}
                </>
              )}
            </>
          )}
          {model.ShowPaymentMethodSubsection && model.PaymentMethodModel && (
            <CheckoutConfirmationPaymentMethodSubsection model={model.PaymentMethodModel} />
          )}
        </>
      )}
    </CheckoutConfirmationLayout>
  );
};
